import { motion } from "framer-motion";
import { Landmark, Leaf, Cpu, Heart } from "lucide-react";
import Seo from "@/components/Seo";
import { IMG } from "@/i18n";

const EASE = [0.22, 1, 0.36, 1];
const VALUE_ICONS = [Landmark, Leaf, Cpu, Heart];

const Reveal = ({ children, delay = 0, ...rest }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, ease: EASE, delay }}
    {...rest}
  >
    {children}
  </motion.div>
);

export default function StoryPage({ t }) {
  const s = t.storyPage;

  return (
    <main data-testid="story-page">
      <Seo title="Our Story — Kurkuma AI Kitchen" siteName="Kurkuma AI Kitchen" description={s.heroSub} />

      <section className="relative overflow-hidden bg-navy text-cream" data-testid="story-hero">
        <img src={IMG.catCorporate} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 md:px-8 md:py-20">
          <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <h1 className="font-display text-4xl font-bold tracking-tight md:text-5xl" data-testid="story-heading">{s.heroTitle}</h1>
            <p className="mt-2 text-cream/70">{s.heroSub}</p>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 md:px-8" data-testid="story-content">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <img
              src={IMG.story}
              alt="Fresh preparation in the Kurkuma kitchen"
              className="aspect-[4/3] w-full rounded-3xl object-cover shadow-lg"
              data-testid="story-image"
            />
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-5 text-base leading-relaxed text-ink/70 md:text-lg">
              {s.paras.map((p) => (
                <p key={p.slice(0, 24)}>{p}</p>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal className="mt-20 flex flex-col items-center gap-8 text-center md:flex-row md:justify-center md:text-left" data-testid="story-motto">
          <p className="font-display text-3xl font-bold italic text-branddark md:text-4xl">“{s.motto}”</p>
          <img src={IMG.spices} alt="Fresh turmeric" loading="lazy" className="w-40 rounded-2xl object-cover shadow-md md:w-52" />
        </Reveal>

        <div className="mt-20 grid grid-cols-2 gap-8 md:grid-cols-4" data-testid="story-values">
          {s.values.map((v, i) => {
            const Icon = VALUE_ICONS[i];
            return (
              <Reveal key={v} delay={i * 0.07} className="text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand/15 text-leaf">
                  <Icon className="h-6 w-6" />
                </span>
                <p className="mt-3 font-display text-sm font-semibold text-navy">{v}</p>
              </Reveal>
            );
          })}
        </div>
      </section>
    </main>
  );
}
